<script lang="ts">
  import { AssetPaths } from '../../assets/asset-store';
  import { UPGRADE_DOCK } from '../../game/constants/hud';
  import type { DockSlot } from '../hud-state.svelte';

  const { slots, onbuy }: { slots: readonly DockSlot[]; onbuy: (slot: number) => void } = $props();

  const { button } = UPGRADE_DOCK;

  /**
   * Distance from the viewport's bottom edge to the button's bottom edge, for each state.
   *
   * The original positions these by their **top** edge, `uiHeight - 40 + yOffset`, and the buttons
   * are 100px tall — so a resting button's bottom edge is 49px *below* the viewport and only its
   * top 51px, the label plate, is visible. That is the design: an unaffordable upgrade is tucked
   * into the dock and rises out of it when you can afford it.
   */
  const liftedBottom = -(
    UPGRADE_DOCK.originYFromBottom +
    UPGRADE_DOCK.liftedYOffset +
    button.height
  );
  const restingBottom = -(
    UPGRADE_DOCK.originYFromBottom +
    UPGRADE_DOCK.restingYOffset +
    button.height
  );

  /** Q, W, E, R — the keys these buttons duplicate, shown so the binding is discoverable. */
  const KEYS = ['Q', 'W', 'E', 'R'] as const;
</script>

<!--
  The upgrade dock: three upgrade paths and ship evolution.

  **An affordable button sits 56px higher than an unaffordable one.** That lift is the whole
  affordance in the original and it is kept, as a CSS transition rather than a jump — the position is
  the same, the movement between positions is smoother.

  Unlike the original, the clickable area *is* the button. The original responded only to a 36px
  strip inside each 80px button, so a click near an edge fired the ship's weapon instead; that was a
  consequence of the hit rectangle being a separate copy of the geometry, which is the bug this
  markup makes unrepresentable. `TODO.md` records the change.
-->
<div class="dock">
  {#each slots as entry, index (entry.slot)}
    <button
      class="dock__button"
      type="button"
      style:left="{UPGRADE_DOCK.originXFromCentre + index * button.spacing}px"
      style:bottom="{entry.affordable ? liftedBottom : restingBottom}px"
      style:width="{button.width}px"
      style:height="{button.height}px"
      style:background-image="url({AssetPaths.ui(UPGRADE_DOCK.images[index] ?? 'UpgradeQ')})"
      disabled={entry.cost === null}
      title={entry.name}
      aria-label="{entry.name} — level {entry.level}, {entry.cost === null
        ? 'maxed'
        : `costs ${String(entry.cost)} scrap`} ({KEYS[index]})"
      data-testid="dock-{entry.slot}"
      onclick={() => {
        onbuy(entry.slot);
      }}
    >
      <span class="dock__abbr" style:font-size="{UPGRADE_DOCK.label.font}px">{entry.abbr}</span>
      <span class="dock__level" style:font-size="{UPGRADE_DOCK.level.font}px">{entry.level}</span>
      <span
        class="dock__cost"
        class:dock__cost--unaffordable={!entry.affordable}
        style:font-size="{UPGRADE_DOCK.cost.font}px"
        >{entry.cost === null ? UPGRADE_DOCK.maxLabel : entry.cost}</span
      >
    </button>
  {/each}

  <img class="dock__backing" src={AssetPaths.ui(UPGRADE_DOCK.dock.image)} alt="" />
</div>

<style>
  .dock {
    position: absolute;
    bottom: 0;
    left: 50%;
    height: 40px;
  }

  .dock__backing {
    position: absolute;
    bottom: 0;
    left: -224px;
    width: 442px;
    height: 40px;
  }

  .dock__button {
    position: absolute;
    padding: 0;
    border: 0;
    background-color: transparent;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    color: #111;
    font: inherit;
    pointer-events: auto;
    cursor: pointer;
    transition: bottom 90ms ease-out;
  }

  .dock__button:disabled {
    cursor: default;
  }

  .dock__abbr,
  .dock__level,
  .dock__cost {
    position: absolute;
    line-height: 1;
    white-space: nowrap;
  }

  .dock__abbr {
    top: 12px;
    left: 7px;
  }

  .dock__level {
    top: 14px;
    left: 61px;
    transform: translateX(-50%);
  }

  .dock__cost {
    top: 29px;
    left: 40px;
    transform: translateX(-50%);
  }

  .dock__cost--unaffordable {
    color: #cc4444;
  }
</style>
